import React, { Component } from 'react';
import { func, number, shape, string, objectOf, any } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { get, merge, noop } from 'lodash';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import classNames from 'classnames';

import { trackingInfo, setCustomError } from 'actions/clz';
import { updateSACode, updateBODStep, bodUtmLinkInfo } from 'actions/clz/0bod';
import { verifyShortId } from 'actions/shortURL';
import { urlQueryToString } from 'utils/browser';
import { convertDate } from 'utils/time';
import Alert from './Alert';

import {
  STEPS,
  STEP_NAME,
  STEP_COMPONENT,
  REDIRECT_HC_CASH_LOAN_PARAMS,
} from './constant';
import history from '../../../history';
import CallButton from './CallButton';
import DesktopDesign from './DesktopDesign';
import { goToHCCashLoan } from './utils';

import s from './Home.css';

class FirstBOD extends Component {
  static propTypes = {
    params: shape({
      sa: string,
    }),
    query: objectOf(any),
    updateStep: func,
    updateBodUtmLinkInfo: func,
    setUserError: func,
    step: number,
    trackingToken: string,
    info: objectOf(any),
    onSetSACode: func,
  };

  static defaultProps = {
    params: {
      sa: null,
    },
    updateStep: noop,
    setUserError: noop,
    updateBodUtmLinkInfo: noop,
    onSetSACode: noop,
    step: STEPS.START_LOAN,
    trackingToken: null,
    info: {},
    query: {},
  };

  async componentDidMount() {
    // Save time begin for GA
    this.startTime = Date.now();
    // Parse SA Code
    const {
      params: { sa, code },
      updateBodUtmLinkInfo,
      onSetSACode,
      query,
    } = this.props;
    const saCode = code || sa;

    if (!saCode || REDIRECT_HC_CASH_LOAN_PARAMS.includes(saCode)) {
      goToHCCashLoan();
      return;
    }

    updateBodUtmLinkInfo(urlQueryToString(query));
    if (saCode) {
      try {
        const verify = await verifyShortId(saCode);

        !verify.errors && onSetSACode(merge(verify, { sa: saCode }));

        if (verify.errors.length) {
          this.navigateToWelcomePage();
          onSetSACode();
        }
      } catch (error) {
        this.navigateToWelcomePage();
      }
    }

    if (!saCode) {
      this.navigateToWelcomePage();
      onSetSACode();
    }
  }

  /**
   * Reset 0bod step
   *
   * @return {void}
   */
  componentWillUnmount() {
    this.props.updateStep(STEPS.START_LOAN);

    // Send time begin for GA
    const timeFill = Date.now() - this.startTime;
    window.ga('send', 'timing', {
      timingCategory: 'Application process',
      timingVar: '0BOD application',
      timingValue: timeFill,
    });
  }

  /**
   * Go back to the previous step
   *
   * @return {void}
   */
  onClickBack = () => {
    const { step = STEPS.CALCULATE_CASH, setUserError } = this.props;
    let previousStep = step - 1;
    // Do not allow to back to step 4
    previousStep === STEPS.OTP && (previousStep -= 1);
    if (step === STEPS.OTP) {
      setUserError('');
    }
    this.props.updateStep(previousStep);
    history.push(`/${STEP_NAME[previousStep]}?isBack=true`);
  };

  /**
   * Navigate the browser back to the first page
   */
  navigateToWelcomePage = () => {
    history.push(`/${STEP_NAME[STEPS.START_LOAN]}`);
  };

  /**
   * Go to next step, use in children component
   *
   * @return {void}
   */
  goToNextStep = () => {
    const { step, trackingToken } = this.props;
    const nextStep = step + 1;
    this.props.updateStep(nextStep);
    history.push(`/${STEP_NAME[nextStep]}`);
    if (step === STEPS.PERSONAL_INFORMATION) {
      const trackedInfo = this.resolveDataTracking();
      const data = {
        url: STEP_NAME[step],
        body: trackedInfo,
        token: trackingToken,
      };
      trackingInfo(data);
    }
  };

  resolveDataTracking = () => {
    const {
      info: { firstName, lastName, middleName, genderCode: gender, birthday },
    } = this.props;

    return {
      dayOfBirth: convertDate(new Date(birthday), 'YYYY-MM-DD'),
      firstName,
      gender,
      lastName,
      middleName,
    };
  };

  render() {
    const { step = STEPS.START_LOAN, params } = this.props;
    const ContentComponent = get(STEP_COMPONENT, step);
    const popup = { open: this.onOpenModal, close: this.onCloseModal };
    const callBtnStyle = classNames(s.callBtn, {
      [`${s.callBtnSpec}`]: step === STEPS.START_LOAN,
    });

    return (
      <div className={s.homeWrapper}>
        <DesktopDesign>
          <div className={s.layoutWrapper}>
            <ContentComponent
              goToNextStep={this.goToNextStep}
              onClickBack={this.onClickBack}
              globalPopup={popup}
              params={params}
              onNextClick={this.onClickNext}
            />
          </div>
        </DesktopDesign>
        <CallButton className={callBtnStyle} />
        <Alert />
      </div>
    );
  }
}

const mapStateToProps = ({ clz }) => ({
  step: clz.step,
  trackingToken: clz.trackingToken,
  info: clz.newBOD.user,
});

const mapDispatchToProps = dispatch => ({
  updateStep(step) {
    dispatch(updateBODStep(step));
  },
  updateBodUtmLinkInfo(info) {
    dispatch(bodUtmLinkInfo(info));
  },
  onSetSACode(data) {
    dispatch(updateSACode(data));
  },
  setUserError(error) {
    dispatch(setCustomError(error));
  },
});

const enhance = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withStyles(s),
);

export default enhance(FirstBOD);
